import React from "react";
import { X } from "lucide-react";
import {
  Flyin,
  FlyinBody,
  FlyinHeader,
  FlyinTitle,
} from "../../../../shared/ui";
import {
  ReportTimelineEvent,
  TransactionActivityLogEvent,
  TransactionReportData,
} from "../../types";

export interface ReportDetailsFlyinProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportData?: TransactionReportData | null;
  activityLogs?: TransactionActivityLogEvent[];
  events?: TransactionActivityLogEvent[];
  timeline?: ReportTimelineEvent[];
  isResolved?: boolean;
  onOpenResolveModal?: () => void;
  onOpenEscalateModal?: () => void;
}

export const ReportDetailsFlyin: React.FC<ReportDetailsFlyinProps> = ({
  open,
  onOpenChange,
  reportData,
  activityLogs,
  events,
  timeline: propTimeline,
}) => {
  const reason = reportData?.reason || "Transaction failed, but was debited";
  const description =
    reportData?.description ||
    "I noticed a debit of funds on my account today, even though it showed failed on my end.";
  const dateReported = reportData?.dateReported || "May 11th, 2025";

  const activeLogs = activityLogs || events || [];

  // Fallback timeline items based on reportData or propTimeline
  const fallbackTimeline: ReportTimelineEvent[] =
    propTimeline && propTimeline.length > 0
      ? propTimeline
      : reportData?.timeline && reportData.timeline.length > 0
      ? reportData.timeline
      : [];

  return (
    <Flyin
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="bg-black/60"
      contentClassName="max-w-[500px] rounded-l-[20px]"
    >
      {/* Flyin Header: Title on the left, Close icon at the end of the header on the right */}
      <FlyinHeader className="flex items-center justify-between border-b border-[#F2F4F7] px-6 py-5">
        <FlyinTitle className="text-[20px] font-semibold text-[#101828]">
          Report details
        </FlyinTitle>
        <button
          type="button"
          aria-label="Close report details"
          onClick={() => onOpenChange(false)}
          className="rounded-lg p-1.5 text-[#667085] hover:bg-[#F9FAFB] hover:text-[#101828] transition-colors"
        >
          <X size={20} />
        </button>
      </FlyinHeader>

      <FlyinBody className="overflow-y-auto px-6 py-6">
        {/* Issue Section */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[14px] font-semibold text-[#101828]">Issue</h3>

          <div className="rounded-xl border border-[#F2F4F7] bg-[#F9FAFB] p-4 text-[13px]">
            <div className="grid grid-cols-[100px_1fr] items-start gap-y-4">
              <span className="text-[#667085]">Reason</span>
              <span className="text-right font-semibold text-[#6F00FF]">
                {reason}
              </span>

              <span className="text-[#667085]">Description</span>
              <span className="text-right text-[#344054]">{description}</span>

              <span className="text-[#667085]">Date reported</span>
              <span className="text-right font-medium text-[#101828]">
                {dateReported}
              </span>
            </div>
          </div>
        </section>

        {/* Transaction History / Activity Log Section */}
        <section className="mt-8 flex flex-col gap-4">
          <h3 className="text-[14px] font-semibold text-[#101828]">
            Transaction history
          </h3>

          {activeLogs.length > 0 ? (
            <div className="relative space-y-6 before:absolute before:left-[3px] before:top-2 before:h-[calc(100%-16px)] before:w-px before:bg-[#E4E7F4]">
              {activeLogs.map((event) => {
                const isFinalInitiatedWithoutActor = !event.actorName;

                return (
                  <div key={event.id} className="relative pl-6">
                    <span className="absolute left-0 top-1.5 z-10 h-2 w-2 rounded-full bg-[#D0D5DD] ring-4 ring-white" />

                    {isFinalInitiatedWithoutActor ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] font-semibold text-[#101828]">
                          {event.boldTarget}
                        </span>
                        <span className="text-[11px] text-[#667085]">
                          {event.timestamp}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <span className="text-[11px] text-[#667085]">
                          {event.timestamp}
                        </span>

                        <div className="flex items-center gap-2 text-[13px] text-[#344054]">
                          <div
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                              event.isCustomer
                                ? "bg-[#D1E9FF] text-[#175CD3]"
                                : "bg-[#F4EBFF] text-[#6F00FF]"
                            }`}
                          >
                            {event.actorName
                              ? event.actorName.slice(0, 1).toUpperCase()
                              : "A"}
                          </div>

                          <span>
                            <span className="font-medium text-[#101828]">
                              {event.actorName}
                            </span>{" "}
                            {event.actionPrefix ? `${event.actionPrefix} ` : ""}
                            <strong className="font-semibold text-[#101828]">
                              {event.boldTarget}
                            </strong>
                            {event.actionSuffix ? ` ${event.actionSuffix}` : ""}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : fallbackTimeline.length > 0 ? (
            <div className="relative space-y-7 before:absolute before:left-[5px] before:top-2 before:h-[calc(100%-16px)] before:w-px before:bg-[#E1E6FF]">
              {fallbackTimeline.map((item) => (
                <div key={item.id} className="relative pl-7">
                  <span
                    className={`absolute left-0 top-1.5 z-10 h-2.5 w-2.5 rounded-full ring-4 ring-white ${
                      item.statusDotColor === "green"
                        ? "bg-[#12B76A]"
                        : item.statusDotColor === "amber"
                          ? "bg-[#F79009]"
                          : "bg-[#98A2B3]"
                    }`}
                  />

                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] text-[#667085]">
                      {item.timestamp}
                    </span>

                    <span className="text-[13px] font-semibold text-[#101828]">
                      {item.title}
                    </span>

                    <div className="flex items-center gap-1.5 text-[12px] text-[#344054]">
                      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#E0EAFF] text-[9px] font-bold text-[#1570EF]">
                        {item.actorName.slice(0, 1).toUpperCase()}
                      </div>
                      <span className="font-medium text-[#101828]">
                        {item.actorName}
                      </span>
                      {item.actorRole && (
                        <span className="rounded bg-[#F2F4F7] px-1 text-[10px] text-[#475467]">
                          {item.actorRole}
                        </span>
                      )}
                      {item.description && (
                        <span className="text-[#667085]">{item.description}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[#667085]">No activity logged yet.</p>
          )}
        </section>
      </FlyinBody>
    </Flyin>
  );
};
